import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Play, Youtube, Zap } from "lucide-react";
import { toast } from "sonner";
import CountdownBlocks from "./CountdownBlocks";
import { CHANNEL_URL, SUBSCRIBE_URL, EMBED_URL, SHORT_URL, CHANNEL_HANDLE } from "@/constants/campaign";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const videoY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden px-4 pb-24 pt-28 sm:px-6 sm:pt-36">
      <motion.div style={{ y: bgY, opacity: glowOpacity }} className="pointer-events-none absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHx0cmFkaW5nJTIwdGVybWluYWwlMjBncmFwaCUyMGdsb3dpbmclMjBjaGFydCUyMG5lb258ZW58MHx8fHwxNzkwODk0OTkzfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="h-full w-full object-cover opacity-[0.13]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FFFFFF]/60 via-[#FFFFFF]/80 to-[#FFFFFF]" />
        <div className="absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-red-600/10 blur-[140px]" />
      </motion.div>

      <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-red-600/40 bg-red-600/10 px-4 py-1.5 text-[11px] font-mono font-bold uppercase tracking-[0.25em] text-red-500"
          >
            <Zap className="h-3.5 w-3.5" />
            Official {CHANNEL_HANDLE} YouTube Promo
          </motion.div>

          <h1 data-testid="hero-headline" className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>Subscribe. Like.</MaskedLine>
            <MaskedLine delay={0.3}>Comment. Win your</MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="bg-gradient-to-r from-red-400 via-red-500 to-red-700 bg-clip-text text-transparent">$500 Welcome Bonus</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            FXTM Africa is awarding <strong className="text-red-500">10 random subscribers</strong> with $500 funded
            trading accounts. No sign-up form, no deposit — just 3 simple steps on YouTube before the timer hits zero.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-8 flex flex-col gap-4 sm:flex-row"
          >
            <a
              data-testid="channel-cta-button-hero"
              href={SUBSCRIBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => toast.success(`Opening ${CHANNEL_HANDLE} — hit SUBSCRIBE!`)}
              className="flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-red-500 via-red-500 to-red-600 px-8 py-4 font-black tracking-wide text-white shadow-[0_0_30px_rgba(255,0,0,0.4)] transition-all duration-200 hover:scale-[1.03] hover:shadow-[0_0_45px_rgba(255,0,0,0.6)] active:scale-[0.98]"
            >
              <Youtube className="h-5 w-5" />
              Subscribe & Claim $500
            </a>
            <a
              data-testid="watch-short-cta-hero"
              href={SHORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 rounded-xl bg-red-600 px-8 py-4 font-bold text-white shadow-[0_0_25px_rgba(255,0,0,0.4)] transition-all duration-200 hover:scale-[1.03] hover:bg-red-700 hover:shadow-[0_0_35px_rgba(255,0,0,0.6)] active:scale-[0.98]"
            >
              <Play className="h-5 w-5" />
              Watch the Giveaway Short
            </a>
          </motion.div>

          <div className="mt-12">
            <CountdownBlocks />
          </div>
        </div>

        <motion.div style={{ y: videoY }} className="mx-auto w-full max-w-sm lg:max-w-none">
          <motion.div
            data-testid="youtube-embed-box"
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="red-pulse relative rounded-3xl border-2 border-red-600/60 bg-[#F4F4F5] p-3"
          >
            <div className="mb-3 flex items-center justify-between px-2 pt-1">
              <span className="text-[10px] font-mono font-bold uppercase tracking-[0.25em] text-red-500">
                Must-Watch Giveaway Video
              </span>
              <span className="rounded-full bg-red-600/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-red-400">
                Shorts
              </span>
            </div>
            <div className="overflow-hidden rounded-2xl bg-black">
              <div className="relative aspect-[9/16] max-h-[560px] w-full">
                <iframe
                  data-testid="youtube-iframe"
                  src={EMBED_URL}
                  title="FXTM Africa $500 Giveaway Video"
                  className="absolute inset-0 h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
            <a
              data-testid="open-channel-link"
              href={CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-[#FFFFFF] py-3 text-sm font-bold text-slate-800 transition-colors duration-200 hover:border-red-600/50 hover:text-red-500"
            >
              Open {CHANNEL_HANDLE} Channel
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
